import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './styles.css';
import './overall.css';
import NavRail from './NavRail';
import { TweaksPanel, TweakSection, TweakRadio, TweakToggle, useTweaks } from './TweaksPanel';
import { mergeDefaults } from './themePersist';
import { ALL_ROWS, fmt, ReportRow, RowStatus } from './data';

// Master page — reuses the Overall design language: KPI band on top, pivot-style
// table. Columns are { Расчёт | Master | Δ | Δ% } instead of N companies.

const TWEAK_DEFAULTS = {
  theme: 'paper' as 'paper' | 'light' | 'dark',
  density: 'regular' as 'compact' | 'regular' | 'comfy',
  variant: 'safe' as 'safe' | 'bold',
  hideMatches: false,
  showSparklines: true,
};

const CLUSTERS = [
  { id: 'dd_1q26', label: 'DD · Delta Doors' },
  { id: 'sd_1q26', label: 'SD · Senim Doors' },
  { id: 'kl_1q26', label: 'KL · Kendala Logistics' },
  { id: 'ki_1q26', label: 'KI · Kendala Impex' },
  { id: 'dk_1q26', label: 'DK · Двери.Класс' },
  { id: 'cn_1q26', label: 'CN · ЦН недвижимость' },
];

// Custom 4-column grid for the pivot rows on this page
const MASTER_GRID = '24px minmax(280px, 2fr) 1fr 1fr 1fr 1fr';

function TitleBar({ cluster }: { cluster: string }) {
  return (
    <div className="titlebar">
      <span className="brand">XELENCE</span>
      <span className="sep">/</span>
      <span className="crumb">Master</span>
      <span className="sep">/</span>
      <span className="crumb"><b>{CLUSTERS.find((c) => c.id === cluster)?.label || cluster}</b> · Q1 2026</span>
      <div className="right">
        <div className="stat"><span className="k">master</span><span className="v">master_2026_q1.xlsx</span></div>
        <div className="stat"><span className="k">version</span><span className="v">v.4 · 28.04</span></div>
        <div className="stat"><span className="k">computed</span><span className="v">29 апр 11:42</span></div>
      </div>
    </div>
  );
}

function ClusterFilters({ cluster, setCluster }: { cluster: string; setCluster: (id: string) => void }) {
  return (
    <div className="overall-filters">
      <div className="group">
        <span className="glbl">CLUSTER</span>
        {CLUSTERS.map((c) => (
          <button
            key={c.id}
            className="pill"
            aria-pressed={cluster === c.id}
            onClick={() => setCluster(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="right">
        <span>{ALL_ROWS.length} строк · сверка с master</span>
      </div>
    </div>
  );
}

interface CellSelection {
  rowIdx: number;
  row: ReportRow;
}

interface PivotProps {
  rows: ReportRow[];
  selected: CellSelection | null;
  setSelected: (s: CellSelection | null) => void;
  hideMatches: boolean;
}

function PivotTable({ rows, selected, setSelected, hideMatches }: PivotProps) {
  const visible = hideMatches ? rows.filter((r) => r.status !== 'match') : rows;

  return (
    <div className="pivot">
      <div className="ph" style={{ gridTemplateColumns: MASTER_GRID }}>
        <div></div>
        <div className="first">Статья</div>
        <div className="co">
          <div className="short">CALC</div>
          <div className="full">расчётное значение</div>
          <div className="meta">текущий конфиг</div>
        </div>
        <div className="co">
          <div className="short">MASTER</div>
          <div className="full">из master.xlsx</div>
          <div className="meta">эталон</div>
        </div>
        <div className="co">
          <div className="short">Δ</div>
          <div className="full">расхождение</div>
          <div className="meta">calc − master</div>
        </div>
        <div className="totalh">
          Δ %
          <div className="meta">отклонение от master</div>
        </div>
      </div>

      {visible.map((r) => {
        const ri = rows.indexOf(r);
        const sel = !!selected && selected.rowIdx === ri;
        const diff = r.master !== null ? r.value - (r.master as number) : null;
        const pct = diff !== null && r.master ? (diff / Math.abs(r.master as number)) * 100 : null;
        const statusCls =
          r.status === 'diff' ? 's-diff' :
          r.status === 'over' ? 's-over' :
          r.status === 'match' ? 's-match' : 's-none';
        const isTotal = /итого|чистая|валовая|прибыль до/i.test(r.name);
        return (
          <div
            key={r.uid}
            className={`pr l${r.level} ${isTotal ? 'total' : ''}`}
            style={{ gridTemplateColumns: MASTER_GRID }}
          >
            <div className="lvl"></div>
            <div className="name">
              {r.level >= 1 && <span className="lvl-ind" />}
              {r.name.trim()}
              {r.note && r.status === 'over' && (
                <span title={r.note} style={{ marginLeft: 6, fontSize: 9.5, fontFamily: 'var(--mono)', color: 'var(--override)', border: '1px solid var(--override)', borderRadius: 2, padding: '0 4px', lineHeight: '13px' }}>NOTE</span>
              )}
            </div>
            <div
              className={`cell ${statusCls === 's-diff' ? '' : ''} ${sel ? 'selected' : ''}`}
              onClick={() => setSelected({ rowIdx: ri, row: r })}
            >
              {fmt(r.value)}
            </div>
            <div className="cell" style={{ color: 'var(--ink-3)' }}>
              {r.master === null ? '—' : fmt(r.master)}
            </div>
            <div className={`cell ${statusCls}`}>
              {diff === null ? <span style={{ color: 'var(--ink-4)' }}>—</span>
                : diff === 0 ? <span style={{ color: 'var(--match)' }}>✓ 0</span>
                : fmt(diff)}
            </div>
            <div className="cell total-col" style={{
              color: pct === null ? 'var(--ink-4)'
                : Math.abs(pct) < 0.01 ? 'var(--match)'
                : r.status === 'over' ? 'var(--override)'
                : 'var(--diff)',
            }}>
              {pct === null || !isFinite(pct) ? 'new' : (pct > 0 ? '+' : '') + pct.toFixed(2) + '%'}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DetailOverlay({ sel, onClose }: { sel: CellSelection | null; onClose: () => void }) {
  if (!sel) return null;
  const { row } = sel;
  const diff = row.master !== null ? row.value - (row.master as number) : null;
  const pct = diff !== null && row.master ? (diff / Math.abs(row.master as number)) * 100 : null;
  const valCls = row.status === 'diff' ? 'diff' : row.status === 'over' ? 'over' : '';

  return (
    <div className="detail-overlay">
      <div className="dh">
        <span className="t">Master · {row.name.trim().slice(0, 28)}</span>
        <span className="x" onClick={onClose}>×</span>
      </div>
      <div className="body">
        <div className="row-title">{row.name.trim()}</div>
        <div className="co-line">{row.tableName}</div>
        <div className={`row-val ${valCls}`}>{fmt(diff)}</div>
        <div className="row-sub">
          calc {fmt(row.value)} · master {fmt(row.master)}
          {pct !== null && <> · {(pct > 0 ? '+' : '') + pct.toFixed(2)}%</>}
        </div>

        {row.comment && (
          <>
            <div className="h">Комментарий</div>
            <div style={{ fontFamily: 'var(--sans)', fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
              {row.comment.text}
              <div style={{ marginTop: 4, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--ink-4)' }}>
                {row.comment.author} · {row.comment.ts}
              </div>
            </div>
          </>
        )}

        {row.note && (
          <>
            <div className="h">Note</div>
            <div style={{ fontFamily: 'var(--sans)', fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
              {row.note}
            </div>
          </>
        )}

        {row.sources && row.sources.length > 0 && (
          <>
            <div className="h">Источники</div>
            {row.sources.map((s, i) => (
              <div key={i} className="pl">
                <span className="l">{s.file}!{s.cell}</span>
                <span className="v">{fmt(s.value)}</span>
              </div>
            ))}
          </>
        )}

        <div className="actions">
          <Link className="primary" to="/v2">→ открыть отчёт</Link>
          <button>⤓ экспорт</button>
        </div>
      </div>
    </div>
  );
}

export default function Master() {
  const [t, setTweak] = useTweaks(mergeDefaults(TWEAK_DEFAULTS));
  const [cluster, setCluster] = useState('dd_1q26');
  const [selected, setSelected] = useState<CellSelection | null>(null);

  const counts = useMemo(() => {
    const c: Record<RowStatus, number> = { match: 0, diff: 0, over: 0, none: 0 };
    ALL_ROWS.forEach((r) => { c[r.status]++; });
    return c;
  }, []);

  const totalDiff = useMemo(() => {
    return ALL_ROWS.reduce((sum, r) => {
      if (r.master === null) return sum;
      return sum + (r.value - (r.master as number));
    }, 0);
  }, []);

  const matchPct = ((counts.match / ALL_ROWS.length) * 100).toFixed(0);

  return (
    <div className="shell" data-theme={t.theme} data-density={t.density} data-variant={t.variant}>
      <NavRail />
      <div className="overall-page">
        <TitleBar cluster={cluster} />

        <div className="kpi-band" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          <div className="kpi match">
            <div className="lbl">СОВПАДЕНИЯ</div>
            <div className="val">{counts.match}</div>
            <div className="sub">{matchPct}% строк сходятся с master</div>
            {t.showSparklines && (
              <div className="spark">
                {[0.50, 0.62, 0.78, 0.84, 1.00].map((v, i) => (
                  <div key={i} className={`b ${i === 4 ? 'cur' : ''}`} style={{ height: v * 22 }} />
                ))}
              </div>
            )}
          </div>
          <div className="kpi diff">
            <div className="lbl">РАСХОЖДЕНИЯ</div>
            <div className="val">{counts.diff}</div>
            <div className="sub">требуют проверки</div>
            {t.showSparklines && (
              <div className="spark">
                {[0.92, 0.78, 0.62, 0.45, 0.30].map((v, i) => (
                  <div key={i} className={`b ${i === 4 ? 'cur' : ''}`} style={{ height: v * 22 }} />
                ))}
              </div>
            )}
          </div>
          <div className="kpi">
            <div className="lbl">КОРРЕКТИРОВКИ</div>
            <div className="val" style={{ color: 'var(--override)' }}>{counts.over}</div>
            <div className="sub">ручные override</div>
            {t.showSparklines && (
              <div className="spark">
                {[0.40, 0.55, 0.62, 0.70, 0.80].map((v, i) => (
                  <div key={i} className={`b ${i === 4 ? 'cur' : ''}`} style={{ height: v * 22 }} />
                ))}
              </div>
            )}
          </div>
          <div className="kpi">
            <div className="lbl">ΣΔ ОТКЛОНЕНИЕ</div>
            <div className="val" style={{ color: totalDiff === 0 ? 'var(--match)' : 'var(--diff)' }}>
              {fmt(totalDiff)}
            </div>
            <div className="sub">сумма всех Δ · KZT</div>
          </div>
        </div>

        <ClusterFilters cluster={cluster} setCluster={setCluster} />

        <div className="overall-body">
          <PivotTable
            rows={ALL_ROWS}
            selected={selected} setSelected={setSelected}
            hideMatches={t.hideMatches}
          />
        </div>
      </div>

      <DetailOverlay sel={selected} onClose={() => setSelected(null)} />

      <TweaksPanel>
        <TweakSection label="Тема" />
        <TweakRadio label="Theme" value={t.theme}
          options={['paper', 'light', 'dark']}
          onChange={(v) => setTweak('theme', v)} />
        <TweakSection label="Вариант" />
        <TweakRadio label="Variant" value={t.variant}
          options={['safe', 'bold']}
          onChange={(v) => setTweak('variant', v)} />
        <TweakSection label="Плотность" />
        <TweakRadio label="Density" value={t.density}
          options={['compact', 'regular', 'comfy']}
          onChange={(v) => setTweak('density', v)} />
        <TweakSection label="Вид" />
        <TweakToggle label="Скрыть совпадения" value={t.hideMatches}
          onChange={(v) => setTweak('hideMatches', v)} />
        <TweakToggle label="Sparklines в KPI" value={t.showSparklines}
          onChange={(v) => setTweak('showSparklines', v)} />
      </TweaksPanel>
    </div>
  );
}
